import { type MenuOption } from '@saviynt/design-system';

import {
  type AdvancedFilterState,
  type TabFilterState,
  type WidgetMenuItem,
} from '../types';

import { ADVANCED_FILTERS_METHODS, CF_NEU_BLD, CF_WRN_STL } from './global';

/**
 * Violations tab counts, advanced filters, list sort/actions, side panel, widgets, etc.
 * User-visible `label`, `category`, and fixed option `label` strings double as keys in
 * `shared/constants/messages.ts` → `invertedMessages`; hooks apply `useInvertedLocalize()` at render
 * (e.g. `use-tabfilters`, `use-filterinitialization`, list sort hooks).
 */

// Initial call constants
export const VIOLATIONS_INI_CALL: Record<string, string> = {
  TAB: 'const_for_tab_count_call',
  ADF: 'const_for_ad_filter_call',
  COP: 'const_for_column_picker_call',
  ACS: 'const_for_action_read_only_status',
} as const;

// Tab filters, Advanced filters and Search
export const TABS_FILTERS: Record<string, TabFilterState> = {
  OPENCOUNT: {
    label: 'Open',
    value: 'openCount',
    count: 0,
    isChecked: false,
    isDisabled: false,
    isInitial: false,
    color: 'critical',
  },
  MITIGATEDCOUNT: {
    label: 'Mitigated',
    value: 'mitigatedCount',
    count: 0,
    isChecked: false,
    isDisabled: false,
    isZeroBased: true,
    isInitial: false,
    color: 'info',
  },
  INREVIEWCOUNT: {
    label: 'In Review',
    value: 'inReviewCount',
    count: 0,
    isChecked: false,
    isDisabled: false,
    isZeroBased: true,
    isInitial: false,
    color: 'warning',
  },
  REMEDIATEDCOUNT: {
    label: 'In Remediation',
    value: 'remediatedCount',
    count: 0,
    isChecked: false,
    isDisabled: false,
    isZeroBased: true,
    isInitial: false,
    color: 'neutral',
  },
  CLOSEDCOUNT: {
    label: 'Closed',
    value: 'closedCount',
    count: 0,
    isChecked: false,
    isDisabled: false,
    isZeroBased: true,
    isInitial: false,
    color: 'success',
  },
} as const;

export const TABS_MAPPING: Record<string, string> = {
  [TABS_FILTERS.OPENCOUNT.value]: 'OPEN',
  [TABS_FILTERS.MITIGATEDCOUNT.value]: 'RISK_ACCEPTED',
  [TABS_FILTERS.INREVIEWCOUNT.value]: 'IN_PROGRESS',
  [TABS_FILTERS.REMEDIATEDCOUNT.value]: 'REMEDIATED',
  [TABS_FILTERS.CLOSEDCOUNT.value]: 'CLOSED',
} as const;

/** Maps SOD violation API status to violations list tab filter value. */
export const STATUS_TO_TAB_IN_ACTION: Record<string, string> = {
  OPEN: TABS_FILTERS.OPENCOUNT.value,
  RISK_ACCEPTED: TABS_FILTERS.MITIGATEDCOUNT.value,
  IN_PROGRESS: TABS_FILTERS.INREVIEWCOUNT.value,
  REMEDIATED: TABS_FILTERS.REMEDIATEDCOUNT.value,
  CLOSED: TABS_FILTERS.CLOSEDCOUNT.value,
};

export const RENDER_MODES: Record<string, 'graph' | 'list'> = {
  GRAPH: 'graph',
  LIST: 'list',
} as const;

const VIOLATIONS_TAB_LIST_AD_FILTER_CAT: Record<string, string> = {
  VIOLATIONS: 'Risk/Violation',
  RULESETS: 'Rulesets',
  ACCOUNTS: 'Accounts',
  APPLICATIONS: 'Applications',
  USER: 'User',
  FUNCTIONS: 'Functions',
  ENTITLEMENT: 'Entitlement',
  MITICON: 'Mitigating Control (MC)',
} as const;

export const VIOLATIONS_TAB_LIST_AD_FILTER: Record<
  string,
  AdvancedFilterState
> = {
  RISKNAME: {
    label: 'Risk Name',
    value: 'riskName',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  RISKTYPE: {
    label: 'Risk Type',
    value: 'riskType',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'SOD',
        value: 'SOD',
        isChecked: false,
      },
      {
        label: 'Critical Access',
        value: 'CRITICAL_ACCESS',
        isChecked: false,
      },
      {
        label: 'None',
        value: 'NONE',
        isChecked: false,
      },
    ],
  },
  PRIORITY: {
    label: 'Risk Priority',
    value: 'priority',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    isNoRemoveFilterBtn: true,
    options: [
      {
        label: 'Low',
        value: 'LOW',
        isChecked: false,
      },
      {
        label: 'Medium',
        value: 'MEDIUM',
        isChecked: false,
      },
      {
        label: 'High',
        value: 'HIGH',
        isChecked: false,
      },
      {
        label: 'Critical',
        value: 'CRITICAL',
        isChecked: false,
      },
    ],
  },
  ACTUALVSPOTENTIAL: {
    label: 'Actual vs Potential',
    value: 'ACTUALVSPOTENTIAL',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Actual Risk',
        value: 'Actual Risk',
        isChecked: false,
      },
      {
        label: 'Potential Risk',
        value: 'Potential Risk',
        isChecked: false,
      },
      {
        label: 'Not Calculated',
        value: 'Not Calculated',
        isChecked: false,
      },
    ],
  },
  BUSINESSPROCESS: {
    label: 'Business Process',
    value: 'BUSINESSPROCESS',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  RISKSCORE: {
    label: 'Risk Score',
    value: 'RISKSCORE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    isNoRemoveFilterBtn: true,
    options: [
      {
        label: 'Moderate (25 to 50)',
        value: '[25,50]',
        isChecked: false,
      },
      {
        label: 'High (51-75)',
        value: '[51,75]',
        isChecked: false,
      },
      {
        label: 'Critical (76 - 100)',
        value: '[76,100]',
        isChecked: false,
      },
    ],
  },
  VIOLATIONAGE: {
    label: 'Violation Age',
    value: 'VIOLATIONAGE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    isNoRemoveFilterBtn: true,
    options: [
      {
        label: '0 to 7 days old',
        value: '[0,7]',
        isChecked: false,
      },
      {
        label: '0 to 30 days old',
        value: '[0,30]',
        isChecked: false,
      },
      {
        label: '0 to 60 days old',
        value: '[0,60]',
        isChecked: false,
      },
      {
        label: '0 to 90 days old',
        value: '[0,90]',
        isChecked: false,
      },
    ],
  },
  VIOLATIONORIGIN: {
    label: 'Violation Origin',
    value: 'VIOLATIONORIGIN',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Preventative',
        value: 'PREVENTATIVE',
        isChecked: false,
      },
      {
        label: 'Detective',
        value: 'DETECTIVE',
        isChecked: false,
      },
    ],
  },
  RULESET: {
    label: 'Ruleset',
    value: 'RULESET',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.RULESETS,
    method: ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS,
  },
  NAME: {
    label: 'Account',
    value: 'name',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ACCOUNTS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  ACCOUNTTYPE: {
    label: 'Account Type',
    value: 'accounttype',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ACCOUNTS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      { label: 'A', value: 'A', isChecked: false },
      { label: 'Account type1', value: 'Account type1', isChecked: false },
      { label: 'Account type2', value: 'Account type2', isChecked: false },
      { label: 'B', value: 'B', isChecked: false },
      { label: 'C', value: 'C', isChecked: false },
      { label: 'CsnOnly', value: 'CsnOnly', isChecked: false },
      { label: 'FIREFIGHTERID', value: 'FIREFIGHTERID', isChecked: false },
      { label: 'Internal', value: 'Internal', isChecked: false },
      { label: 'L', value: 'L', isChecked: false },
      { label: 'S', value: 'S', isChecked: false },
      { label: 'service', value: 'service', isChecked: false },
      {
        label: 'Service Account',
        value: 'Service Account',
        isChecked: false,
      },
      { label: 'Standard', value: 'Standard', isChecked: false },
      { label: 'TEST', value: 'TEST', isChecked: false },
    ],
  },
  USERLOCK: {
    label: 'Userlock',
    value: 'userlock',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ACCOUNTS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      { label: 'Locked', value: 'lockedState', isChecked: false },
      { label: 'Unlocked', value: 'unlockedState', isChecked: false },
    ],
  },
  APPLICATION: {
    label: 'Application',
    value: 'APPLICATION',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.APPLICATIONS,
    method: ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS,
  },
  USERNAME: {
    label: 'Username',
    value: 'username',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FIRSTNAME: {
    label: 'First Name',
    value: 'firstname',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  LASTNAME: {
    label: 'Last Name',
    value: 'lastname',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  SYSTEMUSERNAME: {
    label: 'System Username',
    value: 'systemUserName',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  EMPLOYEEID: {
    label: 'Employee ID',
    value: 'employeeid',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  COSTCENTER: {
    label: 'Cost Center',
    value: 'costcenter',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  JOBCODE: {
    label: 'Job Code',
    value: 'jobCode',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  EMAIL: {
    label: 'Email',
    value: 'email',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  DISPLAYNAME: {
    label: 'Display Name',
    value: 'displayname',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  DEPARTMENTNAME: {
    label: 'Department Name',
    value: 'departmentname',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FUNCTION: {
    label: 'Function',
    value: 'FUNCTION',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.FUNCTIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FUNCTIONSTATUS: {
    label: 'Function Status',
    value: 'FUNCTIONSTATUS',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.FUNCTIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Active',
        value: 'ACTIVE',
        isChecked: false,
      },
      {
        label: 'Inactive',
        value: 'INACTIVE',
        isChecked: false,
      },
    ],
  },
  ENTITLEMENT_VALUE: {
    label: 'Entitlement Value Name',
    value: 'entitlement_value',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  ENTITLEMENTTYPE: {
    label: 'Entitlement Type',
    value: 'entitlementType',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  // Not supported from BE
  // SOXCRITICAL: {
  //   label: 'Sox Critical',
  //   value: 'SOXCRITICAL',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
  //   method: ADVANCED_FILTERS_METHODS.FIXED,
  //   options: [
  //     {
  //       label: 'Low',
  //       value: 'LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Medium',
  //       value: 'MEDIUM',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High',
  //       value: 'HIGH',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical',
  //       value: 'CRITICAL',
  //       isChecked: false,
  //     },
  //   ],
  // },
  // SYSCRITICAL: {
  //   label: 'Sys Critical',
  //   value: 'SYSCRITICAL',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
  //   method: ADVANCED_FILTERS_METHODS.FIXED,
  //   options: [
  //     {
  //       label: 'Low',
  //       value: 'LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Medium',
  //       value: 'MEDIUM',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High',
  //       value: 'HIGH',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical',
  //       value: 'CRITICAL',
  //       isChecked: false,
  //     },
  //   ],
  // },
  // CONFIDENTIALITY: {
  //   label: 'Privileged',
  //   value: 'confidentiality',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
  //   method: ADVANCED_FILTERS_METHODS.FIXED,
  //   options: [
  //     {
  //       label: 'Low',
  //       value: 'LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Medium',
  //       value: 'MEDIUM',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High',
  //       value: 'HIGH',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical',
  //       value: 'CRITICAL',
  //       isChecked: false,
  //     },
  //   ],
  // },
  MITIGATINGCTRLNAME: {
    label: 'Mitigating Control Name',
    value: 'MITIGATINGCTRLNAME',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  MCEXPIRYDATE: {
    label: 'MC Expiry Date',
    value: 'MCEXPIRYDATE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Next 24 hours',
        value: '[0,1]',
        isChecked: false,
      },
      {
        label: 'Next 7 days',
        value: '[0,7]',
        isChecked: false,
      },
      {
        label: 'Next 14 days',
        value: '[0,14]',
        isChecked: false,
      },
      {
        label: 'Next 30 days',
        value: '[0,30]',
        isChecked: false,
      },
    ],
  },
  MCSTARTDATE: {
    label: 'MC Assigned Start Date',
    value: 'MCSTARTDATE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Last 24 hours',
        value: '[-1,0]',
        isChecked: false,
      },
      {
        label: 'Last 7 days',
        value: '[-7,0]',
        isChecked: false,
      },
      {
        label: 'Last 14 days',
        value: '[-14,0]',
        isChecked: false,
      },
      {
        label: 'Last 30 days',
        value: '[-30,0]',
        isChecked: false,
      },
    ],
  },
  MCENDDATE: {
    label: 'MC Assigned End Date',
    value: 'MCENDDATE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Next 24 hours',
        value: '[0,1]',
        isChecked: false,
      },
      {
        label: 'Next 7 days',
        value: '[0,7]',
        isChecked: false,
      },
      {
        label: 'Next 14 days',
        value: '[0,14]',
        isChecked: false,
      },
      {
        label: 'Next 30 days',
        value: '[0,30]',
        isChecked: false,
      },
    ],
  },
} as const;

export const DEFAULT_FILTERS: string[] = [
  VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.PRIORITY.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.ACTUALVSPOTENTIAL.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.MITIGATINGCTRLNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value,
] as const;

export const ENTITLEMENT_DEFAULT_FILTERS: string[] = [
  VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENT_VALUE.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.ACTUALVSPOTENTIAL.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value,
] as const;

export const VIOLATIONS_TAB_LIST_INITIAL_FILTERS: AdvancedFilterState[] =
  (() => {
    const isEntitlementContext =
      window.location.pathname.includes('entitlement');

    if (isEntitlementContext) {
      // For entitlement context, preserve order from ENTITLEMENT_DEFAULT_FILTERS for checked filters
      const entitlementFilters = ENTITLEMENT_DEFAULT_FILTERS.map(
        (filterValue) =>
          Object.values(VIOLATIONS_TAB_LIST_AD_FILTER).find(
            (filter) => filter.value === filterValue
          )
      ).filter((filter): filter is NonNullable<typeof filter> =>
        Boolean(filter)
      );

      const remainingFilters = Object.values(
        VIOLATIONS_TAB_LIST_AD_FILTER
      ).filter((filter) => !ENTITLEMENT_DEFAULT_FILTERS.includes(filter.value));

      return [...entitlementFilters, ...remainingFilters].map((filter) => ({
        ...filter,
        isDisabled: false,
        isChecked: ENTITLEMENT_DEFAULT_FILTERS.includes(filter.value),
      }));
    }

    // For non-entitlement context, use the original logic
    return Object.values(VIOLATIONS_TAB_LIST_AD_FILTER).map((filter) => {
      const isDefaultFilter = DEFAULT_FILTERS.includes(filter.value);

      return {
        ...filter,
        isDisabled: false,
        isChecked: isDefaultFilter,
      };
    });
  })();

// Violations tab list actions
interface ListActionState extends MenuOption {
  modalTitle?: string;
  modalSubTitle?: string;
  successTitle?: string;
  successDescription?: string;
  targetTab?: string;
  iconProps?: {
    iconKind: string;
    iconColor: string;
  };
}

export const LIST_ACTIONS: Record<string, ListActionState> = {
  ADDMC: {
    label: 'Add Mitigating Control',
    value: 'ADDMC',
    icon: 'Add',
    modalTitle: 'Mitigate',
    successTitle: 'Mitigating Control(s) Added',
    successDescription: '',
  },
  MITIGATE: {
    label: 'Mitigate',
    value: 'MITIGATE',
    icon: 'SodMitigate',
    modalTitle: 'Mitigate',
    successTitle: 'Mitigating Control Applied',
    successDescription: '',
    targetTab: TABS_FILTERS.MITIGATEDCOUNT.value,
  },
  REMEDIATE: {
    label: 'Remediate',
    value: 'REMEDIATE',
    icon: 'SodRemediate',
    modalTitle: 'Remediate',
    successTitle: 'Remediation In Progress',
    successDescription: 'Violation {riskName} is in remediation.',
    targetTab: TABS_FILTERS.REMEDIATEDCOUNT.value,
  },
  CHNAGEMCENDDATE: {
    label: 'Change Mitigating Control End Date',
    value: 'CHNAGEMCENDDATE',
    icon: 'ExtendEndDate',
    modalTitle: 'Change Mitigating Control End Date',
    successTitle: 'Changed End Date',
    successDescription:
      'End date changed for mitigating controls in violation {riskName}',
    iconProps: {
      iconKind: 'ExtendEndDate',
      iconColor: CF_NEU_BLD,
    },
  },
  ADDREVIEWCOMMENTS: {
    label: 'Add Review Comments',
    value: 'ADDREVIEWCOMMENTS',
    icon: 'Comment',
    modalTitle: 'Add Review Comments',
    successTitle: 'Review Comments Added',
    successDescription: 'Comment added for violation {riskName}',
    iconProps: {
      iconKind: 'Comment',
      iconColor: CF_NEU_BLD,
    },
  },
  CHANGEREVIEWENDDATE: {
    label: 'Change Review End Date',
    value: 'CHANGEREVIEWENDDATE',
    icon: 'ExtendEndDate',
    modalTitle: 'Change Review End Date',
    successTitle: 'Review End Date Changed',
    successDescription: 'End date changed for violation {riskName}',
    iconProps: {
      iconKind: 'Comment',
      iconColor: CF_NEU_BLD,
    },
  },
  REMOVEMC: {
    label: 'Remove Mitigating Control',
    value: 'REMOVEMC',
    icon: 'Delete',
    modalTitle:
      'Remove Mitigating Control{count, plural, =0 {} =1 {} other { ({count})}}?',
    modalSubTitle: 'The violation will be moved to the “Open” status.',
    successTitle: 'Mitigating Control Removed',
    successDescription: 'Violation {riskName} is moved to "Open" status',
    targetTab: TABS_FILTERS.OPENCOUNT.value,
    iconProps: {
      iconKind: 'AlertWarningFilledSeparateColors',
      iconColor: CF_WRN_STL,
    },
  },
  REOPEN: {
    label: 'Re-Open',
    value: 'REOPEN',
    icon: 'SOD',
    modalTitle: 'Re-Open',
    modalSubTitle: 'Move the violation to the “Open” status',
    successTitle: 'Re-Opened',
    successDescription: 'Violation {riskName} is moved to "Open" status',
    targetTab: TABS_FILTERS.OPENCOUNT.value,
    iconProps: { iconKind: 'SOD', iconColor: CF_NEU_BLD },
  },
  MOVETOINREVIEW: {
    label: 'Move to “In Review”',
    value: 'MOVETOINREVIEW',
    icon: 'Comment',
    modalTitle: 'Move to “In Review”',
    modalSubTitle:
      'Move the violation to the “In Review” status to track its progress.',
    successTitle: 'Moved to “In Review”',
    successDescription: 'Violation {riskName} is moved to "In Review" status',
    targetTab: TABS_FILTERS.INREVIEWCOUNT.value,
    iconProps: {
      iconKind: 'Comment',
      iconColor: CF_NEU_BLD,
    },
  },
  GENERATEREPORT: {
    label: 'Generate Report',
    value: 'GENERATEREPORT',
    icon: 'Report',
  },
  VIEWDETAILS: {
    label: 'View Details',
    value: 'VIEWDETAILS',
    icon: 'ArrowRight',
  },
  // Not Actions
  DIVIDER1: {
    label: 'DIVIDER1',
    value: 'DIVIDER1',
    isDivider: true,
  },
  DIVIDER2: {
    label: 'DIVIDER2',
    value: 'DIVIDER2',
    isDivider: true,
  },
  DIVIDER3: {
    label: 'DIVIDER3',
    value: 'DIVIDER3',
    isDivider: true,
  },
} as const;

export const VIOLATIONS_TAB_LIST_ACTIONS: Record<string, MenuOption[]> = {
  [TABS_FILTERS.OPENCOUNT.value]: [
    LIST_ACTIONS.MITIGATE,
    LIST_ACTIONS.REMEDIATE,
    LIST_ACTIONS.MOVETOINREVIEW,
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.DIVIDER1,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.MITIGATEDCOUNT.value]: [
    LIST_ACTIONS.REMEDIATE,
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.DIVIDER1,
    LIST_ACTIONS.CHNAGEMCENDDATE,
    LIST_ACTIONS.ADDMC,
    LIST_ACTIONS.REMOVEMC,
    LIST_ACTIONS.DIVIDER2,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.INREVIEWCOUNT.value]: [
    LIST_ACTIONS.MITIGATE,
    LIST_ACTIONS.REMEDIATE,
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.DIVIDER1,
    LIST_ACTIONS.ADDREVIEWCOMMENTS,
    LIST_ACTIONS.CHANGEREVIEWENDDATE,
    LIST_ACTIONS.REOPEN,
    LIST_ACTIONS.DIVIDER2,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.REMEDIATEDCOUNT.value]: [
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.CLOSEDCOUNT.value]: [
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.VIEWDETAILS,
  ],
} as const;

export const ALLOWED_ACTIONS = {
  ALL: [
    LIST_ACTIONS.MITIGATE.value,
    LIST_ACTIONS.ADDMC.value,
    LIST_ACTIONS.REMEDIATE.value,
    LIST_ACTIONS.CHNAGEMCENDDATE.value,
    LIST_ACTIONS.ADDREVIEWCOMMENTS.value,
    LIST_ACTIONS.CHANGEREVIEWENDDATE.value,
    LIST_ACTIONS.REMOVEMC.value,
    LIST_ACTIONS.REOPEN.value,
    LIST_ACTIONS.MOVETOINREVIEW.value,
    LIST_ACTIONS.GENERATEREPORT.value,
    LIST_ACTIONS.VIEWDETAILS.value,
  ],
  MITIGATE: [
    LIST_ACTIONS.MITIGATE.value,
    LIST_ACTIONS.ADDMC.value,
    LIST_ACTIONS.CHNAGEMCENDDATE.value,
    LIST_ACTIONS.REMOVEMC.value,
    LIST_ACTIONS.VIEWDETAILS.value,
  ],
  REMEDIATE: [LIST_ACTIONS.REMEDIATE.value, LIST_ACTIONS.VIEWDETAILS.value],
  MOVE_TO_IN_REVIEW: [
    LIST_ACTIONS.ADDREVIEWCOMMENTS.value,
    LIST_ACTIONS.CHANGEREVIEWENDDATE.value,
    LIST_ACTIONS.MOVETOINREVIEW.value,
    LIST_ACTIONS.VIEWDETAILS.value,
  ],
  MOVE_TO_OPEN: [LIST_ACTIONS.REOPEN.value, LIST_ACTIONS.VIEWDETAILS.value],
  GENERATE_REPORT: [
    LIST_ACTIONS.GENERATEREPORT.value,
    LIST_ACTIONS.VIEWDETAILS.value,
  ],
};

// List and Sort Action
export interface ViolationsListSortFieldState {
  label: string;
  value: string;
  isChecked?: boolean;
  options?: string[];
}

export const VIOLTIONS_LIST_SORT_ACTIONS: Record<string, MenuOption> = {
  NEWTOOLD: {
    label: 'Newest to Oldest',
    value: 'NEWTOOLD',
  },
  OLDTONEW: {
    label: 'Oldest to Newest',
    value: 'OLDTONEW',
  },
  ATOZ: {
    label: 'A to Z',
    value: 'ATOZ',
  },
  ZTOA: {
    label: 'Z to A',
    value: 'ZTOA',
  },
  MOSTCRITICAL: {
    label: 'Most Critical',
    value: 'MOSTCRITICAL',
  },
  LEASTCRITICAL: {
    label: 'Least Critical',
    value: 'LEASTCRITICAL',
  },
} as const;

export const SORT_ACTION_MAPPING: Record<string, string> = {
  [VIOLTIONS_LIST_SORT_ACTIONS.NEWTOOLD.value]: 'DESC',
  [VIOLTIONS_LIST_SORT_ACTIONS.OLDTONEW.value]: 'ASC',
  [VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value]: 'ASC',
  [VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value]: 'DESC',
  [VIOLTIONS_LIST_SORT_ACTIONS.MOSTCRITICAL.value]: 'DESC',
  [VIOLTIONS_LIST_SORT_ACTIONS.LEASTCRITICAL.value]: 'ASC',
  DESC: 'DESC',
  ASC: 'ASC',
} as const;

export const VIOLTIONS_LIST_SORT_FIELDS: Record<
  string,
  ViolationsListSortFieldState
> = {
  RISKNAME: {
    label: VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.label,
    value: 'riskName',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value,
      VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  // Not supported from BE
  // USERNAME: {
  //   label: VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.label,
  //   value: 'userName',
  //   options: [
  //     VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value,
  //     VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value,
  //   ],
  // },
  // [VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value]: {
  //   label: VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.label,
  //   value: 'riskScore',
  //   options: [
  //     VIOLTIONS_LIST_SORT_ACTIONS.MOSTCRITCAL.value,
  //     VIOLTIONS_LIST_SORT_ACTIONS.LEASTCRITICAL.value,
  //   ],
  // },
  VIOLATIONSAGE: {
    label: 'Violation Age',
    value: 'violationAge',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.NEWTOOLD.value,
      VIOLTIONS_LIST_SORT_ACTIONS.OLDTONEW.value,
    ],
  },
  ACTUALRISK: {
    label: VIOLATIONS_TAB_LIST_AD_FILTER.ACTUALVSPOTENTIAL.label,
    value: 'actualRisk',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value,
      VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  UPDATEDATE: {
    label: 'Update Date',
    value: 'updateDate',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.NEWTOOLD.value,
      VIOLTIONS_LIST_SORT_ACTIONS.OLDTONEW.value,
    ],
  },
};

export const DEFAULT_LIST_COLUMNS: string[] = [
  VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value,
  'riskPriority',
  'actualVsPotential',
  'endpoint',
  'firstName',
  'lastName',
  'accountName',
];

// Side panel
export const VIOLATIONS_LIST_SIDEPANEL_TABS: Record<string, string> = {
  TAB_SUMMARY: 'violations_tab_list_sidepanel_summary_tab',
  TAB_HISTORY: 'violations_tab_list_sidepanel_history_tab',
  TAB_USER: 'violations_tab_list_sidepanel_user_tab',
};

export const VIOLATIONS_TAB_LIST_FOOTER_KIND = {
  FOOTER: 'violations_tab_list_sidepanel_footer',
  LIST: 'violations_tab_list_action',
} as const;

export const AUDIT_TRAIL_MAP = {
  'Moved to In-Review': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Moved to In-Review',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Review Comments Added': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Review Comments Added',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Review End Date Changed': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Review End Date Changed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Requested': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Remediation Requested',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Request Approved': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Remediation Request Approved',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Task Created': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Remediation Task Created',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Re-Opened': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Violation Re-Opened',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Found': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Violation Found',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Request Rejected': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Remediation Request Rejected',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Task Failed': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Remediation Task Failed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Closed': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Violation Closed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Mitigating Control Added': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Mitigating Control Added',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Mitigating Control Removed': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Mitigating Control Removed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Mitigating Control End Date Changed': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Mitigating Control End Date Changed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Mitigated': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Violation Mitigated',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Moved From Status to Close': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Moved From Status to Close',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Moved to In Review': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Moved to In Review',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Moved To In-Remediation': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Moved To In-Remediation',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
};

// Widgets
const WIDGET_KINDS: Record<string, string> = {
  REGULAR: 'regular',
  USER: 'user',
  ENDPOINT: 'endpoint',
  ENTITLEMENT: 'entitlement',
} as const;

export const WIDGET_ACTION: Record<string, string> = {
  HIDE: 'HIDE',
  CUSTOMIZE: 'CUSTOMIZE',
} as const;

export const WIDGETS: Record<string, WidgetMenuItem> = {
  REC_TO_RV: {
    label: 'Recommendations to Reduce Violations',
    value: 'REC_TO_RV',
    description: null,
    icon: 'SaviCompanion',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
  RISK_MV: {
    label: 'Risks with Most Violations',
    value: 'RISK_MV',
    description: null,
    icon: 'CriticalHighRiskHexagon',
    endpoint: '/sod-violations/top-5/widget/risks',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  USER_MV: {
    label: 'Users with Most Violations',
    value: 'USER_MV',
    description: null,
    icon: 'User',
    endpoint: '/sod-violations/top-5/widget/users',
    kind: WIDGET_KINDS.USER,
    isChecked: false,
    isDisabled: false,
  },
  APPLICATION_MV: {
    label: 'Applications with Most Violations',
    value: 'APPLICATION_MV',
    description: null,
    icon: 'Applications',
    endpoint: '/sod-violations/top-5/widget/applications',
    kind: WIDGET_KINDS.ENDPOINT,
    isChecked: false,
    isDisabled: false,
  },
  RULESET_MV: {
    label: 'Rulesets with Most Violations',
    value: 'RULESET_MV',
    description: null,
    icon: 'Policy',
    endpoint: '/sod-violations/top-5/widget/rulesets',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  BP_MV: {
    label: 'Business Process with Most Violations',
    value: 'BP_MV',
    description: null,
    icon: 'Workflow',
    endpoint: '/sod-violations/top-5/widget/business-process',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  ACCOUNT_MV: {
    label: 'Accounts with Most Violations',
    value: 'ACCOUNT_MV',
    description: null,
    icon: 'Account',
    endpoint: '/sod-violations/top-5/widget/accounts',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  ORP_ACC_MV: {
    label: 'Orphan Accounts with Most Violations',
    value: 'ORP_ACC_MV',
    description: null,
    icon: 'Account',
    endpoint: '/sod-violations/top-5/widget/orphan-accounts',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  SVC_ACC_MV: {
    label: 'Service Accounts with Most Violations',
    value: 'SVC_ACC_MV',
    description: null,
    icon: 'Account',
    endpoint: '/sod-violations/top-5/widget/service-accounts',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  ASN_ENT_MV: {
    label: 'Assigned Entitlements Causing Most Violations',
    value: 'ASN_ENT_MV',
    description: null,
    icon: 'Entitlement',
    endpoint: null,
    kind: WIDGET_KINDS.ENTITLEMENT,
    isChecked: false,
    isDisabled: true,
  },
  SOX_CRT_MV: {
    label: 'SOX Critical Entitlements with Most Violations',
    value: 'SOX_CRT_MV',
    description: null,
    icon: 'Entitlement',
    endpoint: null,
    kind: WIDGET_KINDS.ENTITLEMENT,
    isChecked: false,
    isDisabled: true,
  },
  ROLES_MV: {
    label: 'Roles Causing Most Violations',
    value: 'ROLES_MV',
    description: null,
    icon: 'Role',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
  SAP_ROLE_MV: {
    label: 'SAP Roles Causing Most Violations',
    value: 'SAP_ROLE_MV',
    description: 'Specific to SAP',
    icon: 'Entitlement',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
  T_CODE_MV: {
    label: 'T-Codes Causing Most Violations',
    value: 'T_CODE_MV',
    description: 'Specific to SAP',
    icon: 'Entitlement',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
  DEPARTMENT_MV: {
    label: 'Departments with Most Violations',
    value: 'DEPARTMENT_MV',
    description: null,
    icon: 'Organization',
    endpoint: '/sod-violations/top-5/widget/departments',
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: false,
  },
  CC_MV: {
    label: 'Cost Centers with Most Violations',
    value: 'CC_MV',
    description: null,
    icon: 'Finance',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
  LOCATION_MV: {
    label: 'Locations with Most Violations',
    value: 'LOCATION_MV',
    description: null,
    icon: 'LocationOutline',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
  CONTRACTOR_MV: {
    label: 'Contractors with Most Violations',
    value: 'CONTRACTOR_MV',
    description: null,
    icon: 'User',
    endpoint: null,
    kind: WIDGET_KINDS.REGULAR,
    isChecked: false,
    isDisabled: true,
  },
} as const;

export const DEFAULT_WIDGETS: string[] = [
  WIDGETS.BP_MV.value,
  // WIDGETS.DEPARTMENT_MV.value,
  // WIDGETS.APPLICATION_MV.value,
  WIDGETS.USER_MV.value,
  WIDGETS.RISK_MV.value,
];
